import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { Users, Shield, Calendar, Building2, BookOpen, Plus, Edit2, Trash2, Save, X, Clock, RefreshCw, Settings, ArrowRightLeft, AlertTriangle } from 'lucide-react';

export default function ManagerSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({ departments: [], managerUserId: null });
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [glAccounts, setGLAccounts] = useState([]);
  const [glApprovers, setGLApprovers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedGL, setSelectedGL] = useState(null);
  
  // Modal states
  const [showAddApprover, setShowAddApprover] = useState(false);
  const [showEditThreshold, setShowEditThreshold] = useState(null);
  const [showScheduleAbsence, setShowScheduleAbsence] = useState(null);
  const [showAddGLApprover, setShowAddGLApprover] = useState(false);

  // Form states
  const [newApprover, setNewApprover] = useState({ userId: '', approvalOrder: 1, thresholdAmount: 0, currency: 'GBP' });
  const [thresholdForm, setThresholdForm] = useState({ thresholdAmount: 0, currency: 'GBP' });
  const [absenceForm, setAbsenceForm] = useState({ inactiveStartDate: '', inactiveEndDate: '', inactiveReason: '' });

  // Delegation state
  const [delegations, setDelegations] = useState([]);
  const [showAddDelegation, setShowAddDelegation] = useState(null); // approver object being delegated
  const [delegationForm, setDelegationForm] = useState({ delegateUserId: '', startDate: '', endDate: '', reason: '' });

  useEffect(() => {
    fetchOverview();
    fetchAvailableUsers();
    fetchDelegations();
  }, []);

  useEffect(() => {
    if (selectedDepartment) {
      fetchApprovers(selectedDepartment.DepartmentID);
      fetchGLAccounts(selectedDepartment.DepartmentID);
    }
  }, [selectedDepartment]);

  useEffect(() => {
    if (selectedGL && selectedDepartment) {
      fetchGLApprovers(selectedGL.GLAccountID, selectedDepartment.DepartmentID);
    }
  }, [selectedGL, selectedDepartment]);

  const fetchOverview = async () => {
    try {
      const response = await apiClient.get('/manager-settings/overview');
      setOverview(response.data);
      if (response.data.departments.length > 0) {
        setSelectedDepartment(response.data.departments[0]);
      }
    } catch (err) {
      console.error('Error fetching overview:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovers = async (departmentId) => {
    try {
      const response = await apiClient.get(`/manager-settings/approvers/${departmentId}`);
      setApprovers(response.data.approvers);
    } catch (err) {
      console.error('Error fetching approvers:', err);
    }
  };

  const fetchGLAccounts = async (departmentId) => {
    try {
      const response = await apiClient.get(`/manager-settings/gl-accounts/${departmentId}`);
      setGLAccounts(response.data.glAccounts);
    } catch (err) {
      console.error('Error fetching GL accounts:', err);
    }
  };

  const fetchGLApprovers = async (glAccountId, departmentId) => {
    try {
      const response = await apiClient.get(`/manager-settings/gl-accounts/${glAccountId}/${departmentId}/approvers`);
      setGLApprovers(response.data.approvers);
    } catch (err) {
      console.error('Error fetching GL approvers:', err);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await apiClient.get('/manager-settings/available-users');
      setAvailableUsers(response.data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchDelegations = async () => {
    try {
      const response = await apiClient.get('/manager-settings/delegations');
      setDelegations(response.data.delegations || []);
    } catch (err) {
      console.error('Error fetching delegations:', err);
    }
  };

  const handleAddDelegation = async (e) => {
    e.preventDefault();
    if (!showAddDelegation) return;
    try {
      await apiClient.post('/manager-settings/delegations', {
        delegatingUserId: showAddDelegation.UserID,
        delegateUserId: delegationForm.delegateUserId,
        departmentId: selectedDepartment?.DepartmentID || null,
        startDate: delegationForm.startDate,
        endDate: delegationForm.endDate,
        reason: delegationForm.reason
      });
      setShowAddDelegation(null);
      setDelegationForm({ delegateUserId: '', startDate: '', endDate: '', reason: '' });
      fetchDelegations();
    } catch (err) {
      console.error('Error creating delegation:', err);
      alert(err.response?.data?.error || 'Failed to create delegation');
    }
  };

  const handleRemoveDelegation = async (delegationId) => {
    if (!confirm('Remove this delegation?')) return;
    try {
      await apiClient.delete(`/manager-settings/delegations/${delegationId}`);
      fetchDelegations();
    } catch (err) {
      console.error('Error removing delegation:', err);
    }
  };

  const handleAddApprover = async (e) => {
    e.preventDefault();
    if (!selectedDepartment || !newApprover.userId) return;

    try {
      await apiClient.post(`/manager-settings/approvers/${selectedDepartment.DepartmentID}`, newApprover);
      setShowAddApprover(false);
      setNewApprover({ userId: '', approvalOrder: 1, thresholdAmount: 0, currency: 'GBP' });
      fetchApprovers(selectedDepartment.DepartmentID);
      fetchOverview(); // Refresh counts
    } catch (err) {
      console.error('Error adding approver:', err);
      alert(err.response?.data?.error || 'Failed to add approver');
    }
  };

  const handleUpdateThreshold = async (approverId) => {
    try {
      await apiClient.put(`/manager-settings/approvers/${approverId}/threshold`, thresholdForm);
      setShowEditThreshold(null);
      fetchApprovers(selectedDepartment.DepartmentID);
    } catch (err) {
      console.error('Error updating threshold:', err);
      alert(err.response?.data?.error || 'Failed to update threshold');
    }
  };

  const handleScheduleAbsence = async (approverId) => {
    try {
      await apiClient.put(`/manager-settings/approvers/${approverId}/schedule`, absenceForm);
      setShowScheduleAbsence(null);
      setAbsenceForm({ inactiveStartDate: '', inactiveEndDate: '', inactiveReason: '' });
      fetchApprovers(selectedDepartment.DepartmentID);
    } catch (err) {
      console.error('Error scheduling absence:', err);
      alert(err.response?.data?.error || 'Failed to schedule absence');
    }
  };

  const handleToggleActive = async (approverId, currentStatus) => {
    try {
      await apiClient.put(`/manager-settings/approvers/${approverId}/active`, { isActive: !currentStatus });
      fetchApprovers(selectedDepartment.DepartmentID);
    } catch (err) {
      console.error('Error toggling active status:', err);
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleRemoveApprover = async (approverId) => {
    if (!confirm('Are you sure you want to remove this approver?')) return;
    
    try {
      await apiClient.delete(`/manager-settings/approvers/${approverId}`);
      fetchApprovers(selectedDepartment.DepartmentID);
      fetchOverview();
    } catch (err) {
      console.error('Error removing approver:', err);
      alert(err.response?.data?.error || 'Failed to remove approver');
    }
  };

  const handleAddGLApprover = async (e) => {
    e.preventDefault();
    if (!selectedGL || !selectedDepartment || !newApprover.userId) return;

    try {
      await apiClient.post(
        `/manager-settings/gl-accounts/${selectedGL.GLAccountID}/${selectedDepartment.DepartmentID}/approvers`,
        newApprover
      );
      setShowAddGLApprover(false);
      setNewApprover({ userId: '', approvalOrder: 1, thresholdAmount: 0, currency: 'GBP' });
      fetchGLApprovers(selectedGL.GLAccountID, selectedDepartment.DepartmentID);
      fetchGLAccounts(selectedDepartment.DepartmentID); // Refresh counts
    } catch (err) {
      console.error('Error adding GL approver:', err);
      alert(err.response?.data?.error || 'Failed to add GL approver');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Loading settings...</span>
        </div>
      </div>
    );
  }

  if (overview.departments.length === 0) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
          <Building2 className="mx-auto h-10 w-10 text-gray-300 mb-2" />
          <h3 className="text-sm font-medium text-gray-900">No departments</h3>
          <p className="mt-1 text-sm text-gray-500">You are not assigned as a manager for any departments.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-7 h-7 text-blue-600" />
            Department Manager Settings
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage approvers, thresholds, and approval workflows for your departments
          </p>
        </div>
        <button
          onClick={() => { fetchOverview(); fetchAvailableUsers(); }}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Department Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {overview.departments.map((dept) => (
          <div
            key={dept.DepartmentID}
            onClick={() => setSelectedDepartment(dept)}
            className={`bg-white rounded-lg border p-4 cursor-pointer transition-all ${
              selectedDepartment?.DepartmentID === dept.DepartmentID
                ? 'border-blue-500 ring-1 ring-blue-500'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{dept.DepartmentName}</h3>
              </div>
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Manager Threshold:</span>
                <span className="font-semibold text-gray-900">
                  {dept.ManagerThreshold !== null ? `${dept.Currency} ${dept.ManagerThreshold.toLocaleString()}` : 'Not set'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Approvers:</span>
                <span className="font-semibold text-blue-600">{dept.ApproverCount}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">GL Accounts:</span>
                <span className="font-semibold text-green-600">{dept.GLAccountCount}</span>
              </div>
            </div>

            {dept.ManagerThreshold !== null && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                <strong>Note:</strong> Only Finance Admin can modify your threshold
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedDepartment && (
        <>
          {/* Department Approvers Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Department Approvers - {selectedDepartment.DepartmentName}
                </h2>
                <p className="text-sm text-gray-500 mt-1">Manage approval hierarchy and thresholds</p>
              </div>
              <button
                onClick={() => setShowAddApprover(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Approver
              </button>
            </div>

            {approvers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                <p>No approvers configured for this department</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approver</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Threshold</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheduled Absence</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {approvers.map((approver) => (
                      <tr key={approver.ApproverID} className={approver.IsCurrentlyScheduledInactive ? 'bg-yellow-50' : ''}>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-800 font-semibold">
                            {approver.ApprovalOrder}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {approver.FirstName} {approver.LastName}
                              </div>
                              <div className="text-sm text-gray-500">{approver.Email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {approver.ThresholdAmount !== null ? (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-semibold text-gray-900">
                                {approver.Currency} {approver.ThresholdAmount.toLocaleString()}
                              </span>
                              <button
                                onClick={() => {
                                  setShowEditThreshold(approver);
                                  setThresholdForm({ thresholdAmount: approver.ThresholdAmount, currency: approver.Currency });
                                }}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <Edit2 className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setShowEditThreshold(approver);
                                setThresholdForm({ thresholdAmount: 0, currency: 'GBP' });
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              Set threshold
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleActive(approver.ApproverID, approver.ApproverActive)}
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              approver.EffectiveStatus
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                            }`}
                          >
                            {approver.EffectiveStatus ? 'Active' : (approver.IsCurrentlyScheduledInactive ? 'On Leave' : 'Inactive')}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          {approver.InactiveStartDate && approver.InactiveEndDate ? (
                            <div className="flex items-center space-x-2">
                              <div className="text-xs">
                                <div className={approver.IsCurrentlyScheduledInactive ? 'text-orange-600 font-semibold' : 'text-gray-600'}>
                                  {new Date(approver.InactiveStartDate).toLocaleDateString()} - {new Date(approver.InactiveEndDate).toLocaleDateString()}
                                </div>
                                {approver.InactiveReason && (
                                  <div className="text-gray-500">{approver.InactiveReason}</div>
                                )}
                                {approver.IsCurrentlyScheduledInactive && (
                                  <div className="text-orange-600 font-semibold mt-1">⚠️ Currently Absent</div>
                                )}
                              </div>
                              <button
                                onClick={() => {
                                  setShowScheduleAbsence(approver);
                                  setAbsenceForm({
                                    inactiveStartDate: approver.InactiveStartDate?.split('T')[0] || '',
                                    inactiveEndDate: approver.InactiveEndDate?.split('T')[0] || '',
                                    inactiveReason: approver.InactiveReason || ''
                                  });
                                }}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <Edit2 className="h-3 w-3" />
                              </button>
                              {/* Set delegate button when approver has a scheduled absence */}
                              <button
                                onClick={() => {
                                  const existing = delegations.find(d => d.DelegatingUserID === approver.UserID);
                                  setDelegationForm({
                                    delegateUserId: existing?.DelegateUserID || '',
                                    startDate: existing?.StartDate?.split('T')[0] || approver.InactiveStartDate?.split('T')[0] || '',
                                    endDate: existing?.EndDate?.split('T')[0] || approver.InactiveEndDate?.split('T')[0] || '',
                                    reason: existing?.Reason || approver.InactiveReason || ''
                                  });
                                  setShowAddDelegation(approver);
                                }}
                                title="Set delegate"
                                className="text-purple-600 hover:text-purple-800"
                              >
                                <ArrowRightLeft className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setShowScheduleAbsence(approver);
                                setAbsenceForm({ inactiveStartDate: '', inactiveEndDate: '', inactiveReason: '' });
                              }}
                              className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                            >
                              <Calendar className="h-3 w-3 mr-1" />
                              Schedule
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right space-x-2">
                          <button
                            onClick={() => handleRemoveApprover(approver.ApproverID)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Delegations Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ArrowRightLeft className="h-5 w-5 text-purple-600" />
                  Approval Delegations
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  When an approver is absent, their approvals are automatically routed to the designated delegate.
                </p>
              </div>
            </div>

            {delegations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ArrowRightLeft className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm">No active delegations. Use the <strong>↔</strong> button next to an approver's absence to set a delegate.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Absent Approver</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delegate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remove</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {delegations.map(d => {
                      const today = new Date();
                      const active = new Date(d.StartDate) <= today && today <= new Date(d.EndDate);
                      return (
                        <tr key={d.DelegationID} className={active ? 'bg-purple-50' : ''}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {d.DelegatingUserName}
                            {active && <span className="ml-2 text-xs text-purple-600 font-semibold">⚡ Active now</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{d.DelegateUserName}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{d.DepartmentName || 'All departments'}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            {new Date(d.StartDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            {' – '}
                            {new Date(d.EndDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{d.Reason || '—'}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleRemoveDelegation(d.DelegationID)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* GL Accounts Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-green-600" />
                GL Accounts - {selectedDepartment.DepartmentName}
              </h2>
              <p className="text-sm text-gray-500 mt-1">Configure custom approvers for specific GL accounts</p>
            </div>

            {glAccounts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <BookOpen className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                <p>No GL accounts assigned to this department</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {glAccounts.map((gl) => (
                  <div
                    key={gl.GLAccountID}
                    onClick={() => setSelectedGL(gl)}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedGL?.GLAccountID === gl.GLAccountID
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold text-gray-900">{gl.AccountCode}</div>
                        <div className="text-sm text-gray-600 mt-1">{gl.AccountName}</div>
                        <div className="text-xs text-gray-500 mt-1">{gl.AccountType}</div>
                      </div>
                      {gl.CustomApproverCount > 0 && (
                        <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
                          {gl.CustomApproverCount} custom
                        </span>
                      )}
                    </div>
                    {!gl.UseCustomApprovers && (
                      <div className="mt-2 text-xs text-gray-500">
                        Using department default approvers
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* GL Approvers for Selected GL Account */}
            {selectedGL && (
              <div className="mt-6 border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Custom Approvers for {selectedGL.AccountCode} - {selectedGL.AccountName}
                  </h3>
                  <button
                    onClick={() => setShowAddGLApprover(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-600 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add GL Approver
                  </button>
                </div>

                {glApprovers.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                    <p className="text-sm">No custom approvers configured. Using department defaults.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Approver</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Threshold</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {glApprovers.map((approver) => (
                          <tr key={approver.GLApproverID} className={approver.IsCurrentlyScheduledInactive ? 'bg-yellow-50' : ''}>
                            <td className="px-3 py-2 text-sm">{approver.ApprovalOrder}</td>
                            <td className="px-3 py-2 text-sm">
                              {approver.FirstName} {approver.LastName}
                            </td>
                            <td className="px-3 py-2 text-sm">
                              {approver.ThresholdAmount !== null
                                ? `${approver.Currency} ${approver.ThresholdAmount.toLocaleString()}`
                                : 'Not set'}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                approver.ApproverActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {approver.ApproverActive ? 'Active' : 'Inactive'}
                              </span>
                              {approver.IsCurrentlyScheduledInactive && (
                                <span className="ml-2 text-xs text-orange-600">⚠️ Absent</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Approver Modal */}
      {showAddApprover && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Add Department Approver</h3>
            <form onSubmit={handleAddApprover} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select User</label>
                <select
                  value={newApprover.userId}
                  onChange={(e) => setNewApprover({ ...newApprover, userId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">-- Select User --</option>
                  {availableUsers.map((u) => (
                    <option key={u.UserID} value={u.UserID}>
                      {u.FirstName} {u.LastName} ({u.Email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Approval Order</label>
                <input
                  type="number"
                  value={newApprover.approvalOrder}
                  onChange={(e) => setNewApprover({ ...newApprover, approvalOrder: parseInt(e.target.value) })}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Threshold Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={newApprover.thresholdAmount}
                  onChange={(e) => setNewApprover({ ...newApprover, thresholdAmount: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">0 = All invoices, &gt;0 = Only invoices above this amount</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={newApprover.currency}
                  onChange={(e) => setNewApprover({ ...newApprover, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="GBP">GBP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="PLN">PLN</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddApprover(false);
                    setNewApprover({ userId: '', approvalOrder: 1, thresholdAmount: 0, currency: 'GBP' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Approver
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Threshold Modal */}
      {showEditThreshold && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">
              Edit Threshold - {showEditThreshold.FirstName} {showEditThreshold.LastName}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Threshold Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={thresholdForm.thresholdAmount}
                  onChange={(e) => setThresholdForm({ ...thresholdForm, thresholdAmount: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={thresholdForm.currency}
                  onChange={(e) => setThresholdForm({ ...thresholdForm, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="GBP">GBP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="PLN">PLN</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditThreshold(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateThreshold(showEditThreshold.ApproverID)}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Absence Modal */}
      {showScheduleAbsence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-orange-600" />
              Schedule Absence - {showScheduleAbsence.FirstName} {showScheduleAbsence.LastName}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={absenceForm.inactiveStartDate}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, inactiveStartDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Return Date)</label>
                <input
                  type="date"
                  value={absenceForm.inactiveEndDate}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, inactiveEndDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <input
                  type="text"
                  placeholder="e.g., Holiday, Sick Leave"
                  value={absenceForm.inactiveReason}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, inactiveReason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                <Clock className="h-4 w-4 inline mr-1" />
                <strong>Auto-activation:</strong> Approver will automatically become active again after the end date.
                Approvals will bypass this approver during the scheduled period.
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowScheduleAbsence(null);
                    setAbsenceForm({ inactiveStartDate: '', inactiveEndDate: '', inactiveReason: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                {absenceForm.inactiveStartDate || absenceForm.inactiveEndDate ? (
                  <button
                    onClick={() => handleScheduleAbsence(showScheduleAbsence.ApproverID)}
                    className="px-4 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                  >
                    Save Schedule
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      // Clear schedule
                      setAbsenceForm({ inactiveStartDate: '', inactiveEndDate: '', inactiveReason: '' });
                      handleScheduleAbsence(showScheduleAbsence.ApproverID);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear Schedule
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add GL Approver Modal */}
      {showAddGLApprover && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Add GL Account Approver</h3>
            <p className="text-sm text-gray-600 mb-4">
              Custom approver for: <strong>{selectedGL?.AccountCode} - {selectedGL?.AccountName}</strong>
            </p>
            <form onSubmit={handleAddGLApprover} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select User</label>
                <select
                  value={newApprover.userId}
                  onChange={(e) => setNewApprover({ ...newApprover, userId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">-- Select User --</option>
                  {availableUsers.map((u) => (
                    <option key={u.UserID} value={u.UserID}>
                      {u.FirstName} {u.LastName} ({u.Email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Approval Order</label>
                <input
                  type="number"
                  value={newApprover.approvalOrder}
                  onChange={(e) => setNewApprover({ ...newApprover, approvalOrder: parseInt(e.target.value) })}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Threshold Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={newApprover.thresholdAmount}
                  onChange={(e) => setNewApprover({ ...newApprover, thresholdAmount: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddGLApprover(false);
                    setNewApprover({ userId: '', approvalOrder: 1, thresholdAmount: 0, currency: 'GBP' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add GL Approver
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Add Delegation Modal */}
      {showAddDelegation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-1 flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-purple-600" />
              Set Approval Delegate
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Approvals for <strong>{showAddDelegation.FirstName} {showAddDelegation.LastName}</strong> will be routed to the chosen delegate during the selected period.
            </p>
            <form onSubmit={handleAddDelegation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delegate User</label>
                <select
                  value={delegationForm.delegateUserId}
                  onChange={(e) => setDelegationForm({ ...delegationForm, delegateUserId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="">-- Select delegate --</option>
                  {availableUsers
                    .filter(u => u.UserID !== showAddDelegation.UserID)
                    .map(u => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.FirstName} {u.LastName} ({u.Email})
                      </option>
                    ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                  <input
                    type="date"
                    value={delegationForm.startDate}
                    onChange={(e) => setDelegationForm({ ...delegationForm, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Until</label>
                  <input
                    type="date"
                    value={delegationForm.endDate}
                    onChange={(e) => setDelegationForm({ ...delegationForm, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                <input
                  type="text"
                  placeholder="e.g., Annual leave, Sick leave"
                  value={delegationForm.reason}
                  onChange={(e) => setDelegationForm({ ...delegationForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm text-purple-800 flex gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>The delegate will receive all approval notifications and can approve on behalf of the absent approver.</span>
              </div>
              <div className="flex justify-end gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddDelegation(null); setDelegationForm({ delegateUserId: '', startDate: '', endDate: '', reason: '' }); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Confirm Delegation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
